import { useEffect, useState } from 'react';
import { formatDate, formatTime, formatBytes } from '../utils/misc.js';
import { projectStorage, type ProjectSummary } from '../audio/ProjectStorageEngine.js';
import { Icon } from '../components/Icon.js';
import { useStudioStore } from '../stores/useStudioStore.js';

interface ProjectsProps {
  onOpen: (id: string) => void;
  onBack: () => void;
}

export function Projects({ onOpen, onBack }: ProjectsProps) {
  const projects = useStudioStore((s) => s.projectList as ProjectSummary[]);
  const setProjects = useStudioStore((s) => s.setProjectList);
  const setToast = useStudioStore((s) => s.setToast);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    const list = await projectStorage.listProjects();
    setProjects(list);
    setLoading(false);
  };

  useEffect(() => { void refresh(); }, []);

  const remove = async (p: ProjectSummary) => {
    if (!window.confirm(`Apagar “${p.title}” deste dispositivo? Essa ação não pode ser desfeita.`)) return;
    setDeleting(p.id);
    try {
      await projectStorage.deleteProject(p.id);
      await refresh();
      setToast({ tone: 'success', title: 'Projeto apagado', description: 'O arquivo foi removido do armazenamento local.' });
    } catch {
      setToast({ tone: 'error', title: 'Não foi possível apagar', description: projectStorage.lastError ?? 'Tente novamente.' });
    } finally {
      setDeleting(null);
    }
  };

  return (
    <main className="projects-page">
      <header className="projects-header">
        <button className="brand brand-button" onClick={onBack}><span className="brand-mark"><Icon name="logo" size={19} /></span><span className="brand-name">VOCAL<span>STUDIO</span></span></button>
        <button className="btn btn-quiet" onClick={onBack}><Icon name="chevronLeft" size={16} /> Voltar</button>
      </header>
      <div className="projects-main">
        <div className="projects-title-row">
          <div><span className="section-kicker">ARMAZENADOS NESTE DISPOSITIVO</span><h1>Meus projetos<span className="projects-count">{projects.length.toString().padStart(2, '0')}</span></h1><p>Os arquivos ficam no seu navegador. Não são enviados para a nuvem.</p></div>
          <button className="btn btn-outline" onClick={refresh}><Icon name="refresh" size={16} /> Atualizar</button>
        </div>
        {loading ? <div className="empty-projects"><span className="button-spinner" /> Lendo seu armazenamento local…</div> : projects.length ? (
          <div className="project-list">
            {projects.map((p) => (
              <article className="project-row" key={p.id}>
                <div className={`project-art project-art-${p.sourceType}`}>
                  {p.youtubeVideoId ? <img src={`https://i.ytimg.com/vi/${p.youtubeVideoId}/mqdefault.jpg`} alt="" /> : <span className="project-art-wave">{Array.from({length:13},(_,i)=><i key={i} style={{height:`${12+Math.abs(Math.sin(i*1.7))*25}px`}} />)}</span>}
                  <span className="project-art-mark"><Icon name={p.sourceType === 'youtube' ? 'youtube' : 'wave'} size={16} /></span>
                </div>
                <div className="project-info"><div className="project-name-row"><h3>{p.title || 'Projeto sem nome'}</h3><span className="source-tag">{p.sourceType === 'youtube' ? 'YOUTUBE' : p.sourceType === 'file' ? 'INSTRUMENTAL' : 'RÁPIDO'}</span></div><p>{formatDate(p.updatedAt)} <span>·</span> {formatTime(p.durationSec)} <span>·</span> {p.takes} take{p.takes === 1 ? '' : 's'} <span>·</span> {formatBytes(p.bytes)}</p></div>
                <div className="project-row-actions"><button className="btn btn-primary" onClick={() => onOpen(p.id)}>Abrir <Icon name="arrowRight" size={16} /></button><button className="icon-btn danger-icon" disabled={deleting === p.id} onClick={() => remove(p)} title="Apagar projeto"><Icon name="trash" size={16} /></button></div>
              </article>
            ))}
          </div>
        ) : (
          <div className="empty-projects"><div className="empty-project-icon"><Icon name="folder" size={26} /></div><b>Seus projetos aparecem aqui.</b><span>Grave seu primeiro take e o VOCAL STUDIO salva o projeto neste navegador.</span><button className="btn btn-primary" onClick={onBack}><Icon name="mic" size={16} /> Começar agora</button></div>
        )}
      </div>
    </main>
  );
}
